import React, { useContext } from 'react'
import invariant from 'tiny-invariant'

import CruiseV2SavedPriceDetails from './CruiseV2SavedPriceDetails'
import DeepLinkButtons from '../Common/DeepLinkButtons'
import ItineraryItemCard from '../ItineraryItemCard'

import InlineGroup from 'components/Common/InlineGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import CruiseV2OfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/CruiseV2OfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import { CruiseItem, LETripItem } from 'tripPlanner/types/tripItem'
import {
  formatDatesAndDays,
  formatStartAndEndLocation,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/deepLink'

interface Props {
  item: Extract<LETripItem, CruiseItem>
}

export default function CruiseV2ItemCard({ item }: Props) {
  const timeFormat = useRegionTimeFormat()

  const viewAsTemplate = useContext(TripContext)?.viewAsTemplate

  invariant(
    item.savedItemData.offerType === 'cruise',
    'CruiseV2ItemCard can only be used for cruise V2 offer',
  )

  const timeDescription: string | undefined = formatDatesAndDays(
    item,
    timeFormat,
  )

  if (item.isBooked) {
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription]}
      />
    )
  }

  const tileLink: string = buildProductLink(item) ?? ''
  const { priceDisplayType, price, currencyCode, cabinCategory, imageId } =
    item.savedItemData

  let priceAreaContent: JSX.Element | null = null

  if (viewAsTemplate) {
    priceAreaContent = (
      <CruiseV2OfferPriceDetails offerId={item.savedItemData.offerId} />
    )
  } else {
    priceAreaContent = price ? (
      <CruiseV2SavedPriceDetails
        price={price}
        currencyCode={currencyCode}
        priceDisplayType={priceDisplayType}
        cabinCategory={cabinCategory}
      />
    ) : null
  }

  const ctaAreaContent = isLeBookmark(item) ? (
    <InlineGroup vertical="center" horizontal="between" spacing="md">
      <SaveTripItemButton item={item} />
      <DeepLinkButtons item={item} />
    </InlineGroup>
  ) : null

  const locationDescription = (
    <BodyText variant="small" lineClamp={2}>
      {item.startPlace.name}
    </BodyText>
  )

  return (
    <SavedOfferCard
      item={item}
      name={item.name}
      imgId={imageId}
      locationDescription={locationDescription}
      descriptors={[timeDescription]}
      priceAreaContent={priceAreaContent}
      ctaAreaContent={ctaAreaContent}
      to={tileLink}
      testId="saved-cruise-card"
    />
  )
}
